import {
  Component, inject, signal, viewChild,
  ElementRef, effect, untracked
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageBarService, PageBarTab } from 'src/app/services/page-bar.service';
import { DeviceService } from 'src/app/services/device.service';
import { NavLeftService } from 'src/app/services/nav-left.service';

@Component({
  selector:    'app-page-bar',
  standalone:  true,
  imports:     [CommonModule],
  templateUrl: './page-bar.component.html',
  styleUrls:   ['./page-bar.component.scss'],
})
export class PageBarComponent {
  protected bar    = inject(PageBarService);
  protected device = inject(DeviceService);
  private   navLeft = inject(NavLeftService);

  readonly tabsWrapper     = viewChild<ElementRef<HTMLElement>>('tabsWrapper');
  readonly scrollableInner = viewChild<ElementRef<HTMLElement>>('scrollableInner');

  // ── État scroll ───────────────────────────────────────────────────────────
  protected canScrollLeft  = signal(false);
  protected canScrollRight = signal(false);

  constructor() {
    effect((onCleanup) => {
      const inner   = this.scrollableInner();
      const wrapper = this.tabsWrapper();

      if (!inner || !wrapper) {
        untracked(() => this.checkScrollButtons());
        return;
      }

      const el = inner.nativeElement;
      const onScroll = () => this.checkScrollButtons();
      el.addEventListener('scroll', onScroll, { passive: true });

      const observer = new ResizeObserver(() => this.checkScrollButtons());
      observer.observe(wrapper.nativeElement);

      untracked(() => this.checkScrollButtons());

      onCleanup(() => {
        el.removeEventListener('scroll', onScroll);
        observer.disconnect();
      });
    });

    // Réagit aux changements de device sans setTimeout
    effect(() => {
      this.device.device(); // souscription signal
      // untracked pour ne pas créer de dépendance circulaire
      untracked(() => this.checkScrollButtons());
    });
  }

  // ── Logique scroll ────────────────────────────────────────────────────────

  checkScrollButtons(): void {
    const el = this.scrollableInner()?.nativeElement;

    if (!el) {
      this.canScrollLeft.set(false);
      this.canScrollRight.set(false);
      return;
    }

    this.canScrollLeft.set(el.scrollLeft > 0);
    this.canScrollRight.set(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }

  scrollTabs(direction: 'left' | 'right'): void {
    const el = this.scrollableInner()?.nativeElement;
    if (!el) return;

    el.scrollLeft += direction === 'left' ? -200 : 200;
    // scrollend event — plus fiable que setTimeout
    el.addEventListener('scrollend', () => this.checkScrollButtons(), { once: true });
  }

  // ── Callbacks ─────────────────────────────────────────────────────────────

  onTab(tab: PageBarTab): void {
    this.bar.setActiveTab(tab.id);
    this.bar.config().onTabClick?.(tab);
  }

  onBack(): void {
    this.bar.config().onBack?.();
    this.navLeft.showMobileNav();
  }
}